import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import ModalConfirm from './ModalConfirm';
import ModalResult from './ModalResult';

const ActionWithConfirmation = ({
  // Props per il pulsante
  children,
  variant = 'contained',
  color = 'primary',
  disabled = false,
  startIcon,
  endIcon,
  size = 'medium',
  fullWidth = false,
  sx = {},
  
  // Props per la conferma
  confirmTitle,
  confirmMessage,
  confirmButtonText = 'Conferma',
  confirmButtonColor = 'error',
  
  // Props per il risultato
  resultTitle,
  resultMessage,
  resultSeverity = 'info',
  
  // Props per l'azione
  onAction,
  onSuccess,
  onError,
  onCancel,
  
  // Props per il comportamento
  showLoadingOnButton = true,
  closeOnSuccess = true,
  closeOnError = true,
  resetOnClose = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState({ message: '', severity: 'info' });

  const handleButtonClick = () => {
    if (confirmTitle && confirmMessage) {
      setShowConfirm(true);
    } else {
      executeAction();
    }
  };

  const executeAction = async () => {
    try {
      setLoading(true);
      setShowConfirm(false);
      
      const result = await onAction();
      
      // Gestisci il risultato
      if (result) {
        setResultData({
          message: result.message || resultMessage || 'Operazione completata con successo',
          severity: result.severity || 'success'
        });
      } else {
        setResultData({
          message: resultMessage || 'Operazione completata con successo',
          severity: 'success'
        });
      }
      
      setShowResult(true);
      onSuccess?.(result);
      
    } catch (error) {
      console.error('Errore nell\'esecuzione dell\'azione:', error);
      
      let errorMessage = resultMessage || 'Si è verificato un errore';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResultData({
        message: errorMessage,
        severity: 'error'
      });
      
      setShowResult(true);
      onError?.(error);
      
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    executeAction();
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    onCancel?.();
  };

  const handleResultClose = () => {
    setShowResult(false);
    
    if (resultData.severity === 'success' && closeOnSuccess) {
      // Reset dello stato se necessario
      if (resetOnClose) {
        setResultData({ message: '', severity: 'info' });
      }
    } else if (resultData.severity === 'error' && closeOnError) {
      // Reset dello stato se necessario
      if (resetOnClose) {
        setResultData({ message: '', severity: 'info' });
      }
    }
  };

  return (
    <>
      <Button
        variant={variant}
        color={color}
        disabled={disabled || loading}
        startIcon={loading && showLoadingOnButton ? <CircularProgress size={20} /> : startIcon}
        endIcon={endIcon}
        size={size}
        fullWidth={fullWidth}
        onClick={handleButtonClick}
        sx={sx}
      >
        {children}
      </Button>

      <ModalConfirm
        open={showConfirm}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />

      <ModalResult
        open={showResult}
        onClose={handleResultClose}
        message={resultData.message}
        severity={resultData.severity}
        title={resultTitle || (resultData.severity === 'success' ? 'Successo' : 'Errore')}
      />
    </>
  );
};

export default ActionWithConfirmation;
